// JiraUserDetailsModal.js
import React, { useState, useRef } from "react";
import {
  Modal,
  Box,
  Paper,
  Typography,
  IconButton,
  Divider,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Chip,
  Tooltip as MuiTooltip,
  TableSortLabel,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowUpward,
  ArrowDownward,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
  ReferenceLine,
} from "recharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const ISSUE_TYPE_COLORS = {
  Story: "#70C930",
  Bug: "#BA1650",
  Task: "#F9CA00",
  "Sub-task": "#0088FE",
  Unknown: "#AAAAAA",
};

const STATUS_COLORS = {
  "To Do": "#9D419B",
  "In Progress": "#F9CA00",
  Done: "#70C930",
  Blocked: "#BA1650",
  Cancelled: "#666",
};

const JiraUserDetailsModal = ({ open, onClose, user }) => {
  const [orderBy, setOrderBy] = useState("key");
  const [order, setOrder] = useState("asc");
  const modalRef = useRef();

  if (!user) return null;

  const velocityTrendData = user.velocityTrend || [];
  const cycleTimeTrendData = user.cycleTimeTrend || [];
  const issueTypeData = user.issueTypes || [];
  const agingData = calculateTicketAging(user);

  const currentVelocity = velocityTrendData?.at(-1)?.points ?? 0;
  const previousVelocity = velocityTrendData?.at(-2)?.points ?? 0;
  const velocityChange = currentVelocity - previousVelocity;

  const currentCycle = cycleTimeTrendData?.at(-1)?.days ?? 0;
  const previousCycle = cycleTimeTrendData?.at(-2)?.days ?? 0;
  const cycleChange = currentCycle - previousCycle;

  const avgVelocity = Math.round(
    velocityTrendData.reduce((acc, v) => acc + v.points, 0) / (velocityTrendData.length || 1)
  );
  const avgCycleTime = Math.round(
    cycleTimeTrendData.reduce((acc, v) => acc + v.days, 0) / (cycleTimeTrendData.length || 1)
  );

  const renderChange = (value, isBetterWhenLower = false) => {
    const improved = isBetterWhenLower ? value < 0 : value > 0;
    const color = improved ? "#70C930" : "#BA1650";
    const Icon = improved ? ArrowUpward : ArrowDownward;
    return (
      <Icon
        sx={{
          fontSize: 16,
          color,
          ml: 0.5,
          verticalAlign: "middle",
          animation: "fadeInSlide 0.3s ease",
        }}
      />
    );
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedTickets = [...(user.currentTickets || []), ...(user.pastTickets || [])].sort(
    (a, b) => {
      const valA = a[orderBy] ?? "";
      const valB = b[orderBy] ?? "";
      return order === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    }
  );

  const exportToPDF = async () => {
    const canvas = await html2canvas(modalRef.current);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const width = 210;
    const height = (canvas.height * width) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, width, height);
    pdf.save(`${user.displayName}-JiraProfile.pdf`);
  };

  const WrappedTick = ({ x, y, payload }) => {
    const lines = payload.value.split(" ");
    return (
      <g transform={`translate(${x},${y + 10})`}>
        {lines.map((line, i) => (
          <text key={i} x={0} y={i * 12} dy={0} textAnchor="middle" fill="#ccc" fontSize="10px">
            {line}
          </text>
        ))}
      </g>
    );
  };

  const renderTicket = (sortedTickets, title) => {
    return (
      <Box sx={[modalStyles.section]}>
        <Typography variant="subtitle1" sx={modalStyles.sectionTitle}>
          📋 {title} ({sortedTickets.length})
        </Typography>
        <TableContainer
          sx={{
            backgroundColor: "#1E1E1E",
            maxHeight: 300,
            overflowX: "hidden",
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {["key", "summary", "status", "sprint", "resolved"].map((col) => (
                  <TableCell key={col}>
                    <TableSortLabel
                      active={orderBy === col}
                      direction={orderBy === col ? order : "asc"}
                      onClick={() => handleSort(col)}
                    >
                      {col.charAt(0).toUpperCase() + col.slice(1)}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTickets.map((t, i) => (
                <TableRow key={i} hover>
                  <TableCell>{t.key}</TableCell>
                  <TableCell>
                    <MuiTooltip title={t.summary} arrow>
                      <span
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "inline-block",
                          maxWidth: 220,
                        }}
                      >
                        {t.summary}
                      </span>
                    </MuiTooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t.status}
                      size="small"
                      sx={{
                        fontSize: "11px",
                        backgroundColor: STATUS_COLORS[t.status] || "#777",
                        color: "#fff",
                        height: 22,
                      }}
                    />
                  </TableCell>
                  <TableCell>{t.sprint || "n/a"}</TableCell>
                  <TableCell>{t.resolved || ""}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyles.container}>
        <Paper ref={modalRef} sx={modalStyles.paper}>
          <Box sx={modalStyles.header}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#F9CA00" }}>
              Jira User Overview
            </Typography>
            <Box>
              <IconButton onClick={exportToPDF} title="Export as PDF">
                <PdfIcon sx={{ color: "#fff" }} />
              </IconButton>
              <IconButton onClick={onClose}>
                <CloseIcon sx={{ color: "#fff" }} />
              </IconButton>
            </Box>
          </Box>

          <Divider sx={{ backgroundColor: "#777" }} />

          {/* Avatar + Info */}
          <Box sx={modalStyles.section}>
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-block",
                    marginRight: 2,
                  }}
                >
                  <Avatar
                    src={user.avatarUrl}
                    sx={{
                      width: 90,
                      height: 90,
                      border: "2px solid #F9CA00",
                    }}
                  />
                  {user.isHero && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -16,
                        left: "50%",
                        transform: "translateX(-50%)",
                        animation: "pulse 1.2s infinite",
                      }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="#F9CA00">
                        <path d="M4 6L8 14L12 10L16 14L20 6L22 19H2L4 6Z" />
                      </svg>
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid item xs>
                <Typography variant="h6" sx={{ color: "#fff", lineHeight: 1.2 }}>
                  {user.displayName}
                </Typography>
                <Typography variant="body2" sx={{ color: "#ccc" }}>
                  {user.emailAddress}
                </Typography>
                <Typography variant="body2" sx={{ color: "#ccc" }}>
                  {/* clock icon */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                    style={{ marginRight: 4, verticalAlign: "middle" }}
                  >
                    <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8S12.42 0 8 0zm0 14.5A6.5 6.5 0 1 1 14.5 8 6.507 6.507 0 0 1 8 14.5zM7.5 4h1v5H7.5V4zm0-2h1v1h-1V2z" />
                  </svg>
                  {user.timeZone}
                </Typography>
                <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    label={`Velocity: ${currentVelocity}`}
                    sx={modalStyles.chip}
                    icon={renderChange(velocityChange)}
                    size="small"
                  />
                  <Chip
                    label={`Current: ${user.currentSprintTickets}`}
                    sx={modalStyles.chip}
                    size="small"
                  />
                  <Chip
                    label={`Resolved: ${user.previousSprintTickets}`}
                    sx={modalStyles.chip}
                    size="small"
                  />
                  <Chip
                    label={`Cycle: ${currentCycle.toFixed(1)}d`}
                    sx={modalStyles.chip}
                    icon={renderChange(cycleChange, true)}
                    size="small"
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Charts */}
          <Box sx={modalStyles.section}>
            <Grid container spacing={2}>
              {/* Velocity */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={modalStyles.sectionTitle}>
                  📈 Velocity Trend
                </Typography>
                <Typography sx={modalStyles.description}>
                  Tracks story points completed each sprint.
                </Typography>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={velocityTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="sprint" stroke="#ccc" tick={<WrappedTick />} />
                    <YAxis stroke="#ccc" />
                    <Tooltip />
                    <Line type="monotone" dataKey="points" stroke="#F9CA00" strokeWidth={2} />
                    <ReferenceLine
                      y={avgVelocity}
                      label="Avg"
                      stroke="#F9CA00"
                      strokeDasharray="3 3"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Grid>

              {/* Cycle Time */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={modalStyles.sectionTitle}>
                  ⏱ Cycle Time Trend
                </Typography>
                <Typography sx={modalStyles.description}>
                  Avg time to complete issues across sprints.
                </Typography>
                <ResponsiveContainer width="100%" height={156}>
                  <LineChart data={cycleTimeTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="sprint" stroke="#ccc" tick={<WrappedTick />} />
                    <YAxis stroke="#ccc" />
                    <Tooltip />
                    <Line type="monotone" dataKey="days" stroke="#BA1650" strokeWidth={2} />
                    <ReferenceLine
                      y={avgCycleTime}
                      label="Avg"
                      stroke="#BA1650"
                      strokeDasharray="3 3"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Grid>

              {/* Issue Type Breakdown */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={modalStyles.sectionTitle}>
                  📊 Issue Types
                </Typography>
                <Typography sx={modalStyles.description}>
                  Distribution of ticket types worked on.
                </Typography>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={issueTypeData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" stroke="#ccc" />
                    <YAxis type="category" dataKey="type" stroke="#ccc" fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="value">
                      {issueTypeData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={ISSUE_TYPE_COLORS[entry.type] || ISSUE_TYPE_COLORS.Unknown}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Grid>

              {/* Ticket Aging */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={modalStyles.sectionTitle}>
                  📆 Ticket Aging
                </Typography>
                <Typography sx={modalStyles.description}>
                  How old the user’s tickets are.
                </Typography>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={agingData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" stroke="#ccc" />
                    <YAxis type="category" dataKey="bucket" stroke="#ccc" fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#7E4553" />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          </Box>

          {/* Table */}
          <Grid container>
            <Grid item xs={6}>
              {renderTicket(
                sortedTickets.filter(
                  (t) =>
                    t.status == "Done" ||
                    t.status == "Closed" ||
                    t.status == "Resolved" ||
                    t.status == "Cancelled"
                ),
                "Competed Tickets"
              )}
            </Grid>
            <Grid item xs={6}>
              {renderTicket(
                sortedTickets.filter(
                  (t) =>
                    t.status != "Done" &&
                    t.status != "Closed" &&
                    t.status != "Resolved" &&
                    t.status != "Cancelled"
                ),
                "In Progress Tickets"
              )}
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Modal>
  );
};

// Ticket Aging
const calculateTicketAging = (user) => {
  const now = new Date();
  const buckets = {
    "0–3 days": 0,
    "4–7 days": 0,
    "8–14 days": 0,
    "15+ days": 0,
  };
  const tickets = [...(user.currentTickets || []), ...(user.pastTickets || [])];
  tickets.forEach((t) => {
    const updated = new Date(t.updatedAt || t.resolvedAt || t.createdAt);
    const days = (now - updated) / (1000 * 60 * 60 * 24);
    if (days <= 3) buckets["0–3 days"]++;
    else if (days <= 7) buckets["4–7 days"]++;
    else if (days <= 14) buckets["8–14 days"]++;
    else buckets["15+ days"]++;
  });
  return Object.entries(buckets).map(([bucket, count]) => ({ bucket, count }));
};

// Styles
const modalStyles = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.6)",
  },
  paper: {
    width: "92%",
    maxWidth: "1400px",
    backgroundColor: "#303030",
    color: "#fff",
    borderRadius: "8px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.3)",
    maxHeight: "95vh",
    overflowY: "auto",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    backgroundColor: "#303030",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  section: {
    padding: "16px 24px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "bold",
    color: "#F9CA00",
    mb: 0.5,
  },
  description: {
    fontSize: "13px",
    color: "#ccc",
    mb: 1.5,
  },
  chip: {
    backgroundColor: "#505050",
    color: "#fff",
    fontSize: "13px",
  },
};

// Animations
const style = document.createElement("style");
style.innerHTML = `
@keyframes pulse {
  0% { transform: translateX(-50%) scale(1); }
  50% { transform: translateX(-50%) scale(1.1); }
  100% { transform: translateX(-50%) scale(1); }
}
@keyframes fadeInSlide {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}`;
document.head.appendChild(style);

export default JiraUserDetailsModal;
